/* eslint-disable no-nested-ternary */
import { Box, IconButton, TableCell, TableRow, Typography, Collapse, Switch } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import DisbursmentsAccountedCell from './disbursmentsAccounted';
import { formatMoney } from 'utils/helpers';
import DisbursmentsBtns from './disbursmentsBtns';
import { AccountingInvolvedParty, AccountingType } from 'v2-types/order';
import { TFunction } from 'i18next';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Actionbuttons from 'components/common/actions-buttons';
import { InvolvedPartiesTable } from './InvolvedPartiesTable/involved-parties-table';
import { getEntryValues } from '../utils/entry-values';

export const methodBackogrundColors = {
  wire: "#fef5ca",
  netfunded: "#ffdfb2",
  cash: "#e8f5e9",
  check: "#e3f6fc"
};

const useStyles = makeStyles((theme: any) => ({
  containerPosted: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  btn: {
    color: theme.palette.primary.darker,
    fontSize: 16,
    fontWeight: 600,
  },
  date: {
    color: theme.palette.primary.darker,
    fontSize: 14,
    width: 'max-content',
  },
  unAccountedContainer: {
    display: 'flex',
    gap: 10,
    justifyContent: 'space-between',
  },
  text: {
    color: theme.palette.primary.darker,
    fontSize: 14,
    width: 'max-content',
    textTransform: 'capitalize',
  },
  description: {
    color: theme.palette.primary.darker,
    width: 'max-content',
    textTransform: 'capitalize',
    fontWeight: 'bold'
  },
  amountContainer: {
    display: "flex",
    gap: "4px",
    alignItems: "center"
  },
  amount: {
    textTransform: 'capitalize',
    color: theme.palette.error.main,
    fontSize: 14,
    fontWeight: 'bolder',
  },
  cellContainerPosted: {
    borderBottom: "1px solid #cfd8dc"
  },
  editBtn: {
    color: theme.palette.success.main,
    fontSize: 18,
    fontWeight: 600,
  },
  containerDebit: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  method: ({ code }: { code: string }) => ({
    color: theme.palette.primary.darker,
    fontSize: 14,
    width: 'max-content',
    textTransform: 'capitalize',
    padding: "4px 8px",
    borderRadius: "5px",
    backgroundColor: methodBackogrundColors[code] || "gray",
  }),
  letterSpacing: {
    letterSpacing: "2px"
  }
}));

export type DisbursmentRowProps = {
  disbursmentItem: AccountingType;
  handleDeleteEntry: (accountingId: string) => void;
  handleConfirmDelete: (
    openConfirm: boolean,
    callback: (() => void) | null,
    title: string,
    confirmationMessage: string
  ) => void;
  t: TFunction;
  kindEntry: string[];
  handleModals: (modalName: string, idEntry: string, entry?: AccountingType, partyEntry?: AccountingInvolvedParty) => void;
  handleUpdatePosted: (id: string, posted: boolean) => void;
  itemsCollapsed: string[],
  setItemsCollapsed: React.Dispatch<React.SetStateAction<string[]>>,
  openDeleteAssociationModal: (entryId: string, party: AccountingInvolvedParty) => void
};

const DisbursmentRow: FC<DisbursmentRowProps> = ({
  disbursmentItem,
  handleDeleteEntry,
  handleConfirmDelete,
  t,
  kindEntry,
  handleModals,
  handleUpdatePosted,
  itemsCollapsed,
  setItemsCollapsed,
  openDeleteAssociationModal
}) => {
  const classes = useStyles({ code: disbursmentItem.code });

  const { accounted_for, description, posted } = disbursmentItem;

  const { hasAmountFaked, displayedAmount, sumAmountAccounted, isReceipt, methodLabel, entryDateLabel } = getEntryValues(disbursmentItem);

  const isItemCollapsed = itemsCollapsed.some(itemId => itemId === disbursmentItem._id);

  const tableCells = [
    {
      id: "action",
      content: (
        <Actionbuttons
          shouldCollapse={isItemCollapsed}
          handleAdd={() => {
            handleModals("addAssociation", "", disbursmentItem);
          }}
          handleCollapse={(e) => {
            e.stopPropagation();
            setItemsCollapsed((prevItemList) => {
              if (isItemCollapsed) return prevItemList.filter(itemId => itemId !== disbursmentItem._id);
              return [...prevItemList, disbursmentItem._id];
            });
          }}
        />
      ),
      hide: kindEntry.length > 1 || kindEntry.includes('proceeds')
    },
    {
      id: 'description',
      content: <Typography className={classes.description}>{description}</Typography>,
      hide: false,
    },
    {
      id: 'posted',
      content: (
        <Box className={classes.containerPosted}>
          <Switch
            checked={posted}
            onChange={() => {
              handleConfirmDelete(
                true,
                () => handleUpdatePosted(disbursmentItem._id, !posted),
                t('disbursments:titlePosted'),
                t('disbursments:confirmationMessagePosted')
              );
            }}
          />
        </Box>
      ),
      hide: kindEntry.length > 1 || kindEntry.includes('proceeds'),
    },
    {
      id: 'amount',
      content: (
        <Box className={classes.amountContainer}>
          {kindEntry.includes('proceeds') && (
            <IconButton
              className={classes.editBtn}
              onClick={() => handleModals('edit', disbursmentItem._id)}
              size="large"
            >
              <FontAwesomeIcon icon="edit" />
            </IconButton>
          )}
          <Typography className={classes.text}>{formatMoney(displayedAmount)}</Typography>
        </Box>
      ),
      hide: kindEntry.length > 1,
    },
    {
      id: 'method',
      content: <Typography className={classes.method}>{methodLabel}</Typography>,
      hide: kindEntry.includes('proceeds'),
    },
    {
      id: 'date',
      content: (
        <Typography className={classes.date}>{entryDateLabel}</Typography>
      ),
      hide: kindEntry.includes('proceeds'),
    },
    {
      id: 'accounted-for',
      content: <DisbursmentsAccountedCell accounted={accounted_for} t={t} posted={posted} />,
      hide: kindEntry.length > 1 || kindEntry.includes('proceeds'),
    },
    {
      id: 'options',
      content: (
        <DisbursmentsBtns
          handleDeleteEntry={() => handleDeleteEntry(disbursmentItem._id)}
          disbursmentItem={disbursmentItem}
          handleConfirmDelete={handleConfirmDelete}
          t={t}
          handleModals={handleModals}
        />
      ),
      hide: kindEntry.length > 1 || kindEntry.includes('proceeds'),
    },
    {
      id: 'debit',
      content: (
        <Box className={classes.containerDebit}>
          {disbursmentItem.code === 'net_proceeds' && (
            <IconButton
              className={classes.editBtn}
              onClick={() => handleModals('edit', disbursmentItem._id)}
              size="large"
            >
              <FontAwesomeIcon icon="edit" />
            </IconButton>
          )}
          <Typography className={clsx(classes.date, {
            [classes.letterSpacing]: disbursmentItem.kind === 'receipt'
          })}
          >
            {!isReceipt ? formatMoney(sumAmountAccounted) : '------'}
          </Typography>
        </Box>
      ),
      hide: kindEntry.length <= 1,
    },
    {
      id: 'credit',
      content: (
        <Typography className={clsx(classes.date, {
          [classes.letterSpacing]: !(disbursmentItem.kind === 'receipt')
        })}
        >
          {isReceipt ? formatMoney(sumAmountAccounted) : '------'}
        </Typography>
      ),
      hide: kindEntry.length <= 1,
    },
  ];

  return (
    <>
      <TableRow>
        {tableCells
          .filter((cell) => !cell.hide)
          .map((cell) => (
            <TableCell
              className={clsx({
                [classes.cellContainerPosted]:
                  (!posted && cell.id === 'posted') ||
                  (hasAmountFaked && cell.id === 'amount') ||
                  (!posted && cell.id === 'accounted-for'),
              })}
              key={cell.id}
            >
              {cell.content}
            </TableCell>
          ))}
      </TableRow>
      <TableRow>
        <TableCell key={`collapse-column-${disbursmentItem._id}`} colSpan={6} style={{ background: 'white', border: "none", padding: "0px 10px" }}>
          <Collapse in={isItemCollapsed} timeout="auto" unmountOnExit>
            <InvolvedPartiesTable
              ledgerRow={disbursmentItem}
              handleEditLedgerAssociation={({ row }) => handleModals("addAssociation", "", disbursmentItem, row)}
              handleDeleteLedgerAssociation={({ row }) => openDeleteAssociationModal(disbursmentItem._id, row)}
            />
          </Collapse>
        </TableCell>
      </TableRow>
      <TableRow style={{ marginBottom: 16, display: 'table', tableLayout: 'fixed' }} />
    </>
  );
};

export default DisbursmentRow;
